import React, { useEffect, useState } from "react";

import bridge from "@/utils/dsBridge";
import {
	BridgeCallHandlers,
	log2ClientEventName,
	SurveyCompleteTypes,
} from "@/interfaces/enums";
import {
	confirmAccountOpeningAgreement,
	useAnswers,
	useQuestions,
	answerQuestions,
	useMasterAccount,
} from "@/hooks/useRequest/useSurveyRequest";
import { SurveyType } from "@/interfaces/surveyTypes";
import { useRouter } from "next/router";

import log2Sentry from "@/utils/log2Sentry";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { isInApp } from "@/utils/util";

import log2Client from "@/utils/log2Client";

import PolicyView from "./PolicyView";
import { useTranslation } from "next-i18next";
import NoticeModalBottom from "@/containers/common/NoticeModalBottom";
import { shouldBlockMainlandAccountOpening, shouldBlockMainlandAccountOpeningByIp, getIpRegionFromSettings, AppSettingItem } from "./mainlandWhitelist";
import { fetchAppSettings } from "@/service/common.services";

interface PoliciesPageProps {
	unRejectedSubmissions?: boolean;
	afterSubmit?: () => Promise<boolean>;
	ipRegion?: string;
	settings?: AppSettingItem[];
}

export default function PoliciesPage({ afterSubmit, ipRegion, settings }: PoliciesPageProps) {
	const [blockedVisible, setBlockedVisible] = useState(false);
	const [blockedMessageKey, setBlockedMessageKey] = useState("mainland-china-account-opening-not-supported");
	const { t } = useTranslation("survey");
	const { mutate } = useMasterAccount();
	const { answerFieldToValueMap } = useAnswers({
		type: SurveyType.ACCOUNT_OPENING,
	});
	const { questionFieldToIdMap } = useQuestions({
		type: SurveyType.ACCOUNT_OPENING,
	});

	const router = useRouter();

	const showMainlandBlockedModal = (messageKey: string) => {
		setBlockedMessageKey(messageKey);
		setBlockedVisible(true);
		log2sensors(SensorsEventIds.AO_AGREEMENTS_MAINLAND_BLOCKED_VIEW);
	};

	const handleMainlandBlockedConfirm = () => {
		log2sensors(SensorsEventIds.AO_AGREEMENTS_MAINLAND_BLOCKED_CONFIRM);
		setBlockedVisible(false);
	};

	const handleAgreement = async () => {
		if (shouldBlockMainlandAccountOpening(answerFieldToValueMap.get("nationality") || "")) {
			showMainlandBlockedModal("mainland-china-account-opening-not-supported");
			return;
		}
		const settings = await fetchAppSettings();
		if (shouldBlockMainlandAccountOpeningByIp(getIpRegionFromSettings(settings), settings)) {
			showMainlandBlockedModal("cn-restriction-account-opening");
			return;
		}

		try {
			const hasNotFinishedAnswerWithNotChinaTaxResident =
				questionFieldToIdMap.get("notChinaTaxResident") &&
				answerFieldToValueMap.get("notChinaTaxResident") === undefined;

			const hasNotFinishedAnswerWithTaxResidence =
				questionFieldToIdMap.get("taxResidence") && answerFieldToValueMap.get("taxResidence") === undefined;

			if (hasNotFinishedAnswerWithNotChinaTaxResident) {
				await answerQuestions({
					payload: [
						{
							answer: "1",
							questionId: questionFieldToIdMap.get("notChinaTaxResident"),
						},
					],
				});
			}

			// TODO: 兼容处理，开户到一半退出的用户
			if (hasNotFinishedAnswerWithTaxResidence) {
				await answerQuestions({
					payload: [
						{
							answer: answerFieldToValueMap.get("nationality") || "",
							questionId: questionFieldToIdMap.get("taxResidence"),
						},
					],
				});
			}

			await confirmAccountOpeningAgreement(SurveyType.ACCOUNT_OPENING);
			const masterAccount = await mutate();

			if (afterSubmit) {
				await afterSubmit();
			}

			log2Client({
				eventName: log2ClientEventName.openAccountSurveySubmitted,
			});
			bridge.call(BridgeCallHandlers.ON_SURVEY_COMPLETE, {
				questionType: SurveyCompleteTypes.OPEN_ACCOUNT,
			});
			if (masterAccount?.identityRejectReasons?.length == 0 && masterAccount.identityRejectReasons.length !== 0) {
				log2sensors(SensorsEventIds.AO_AGREEMENTS_CONFIRMANDAGREE);
			}

			if (!isInApp()) {
				router.replace("/survey/account-opening/success" + window.location.search);
			}
		} catch (error) {
			log2Sentry(error);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_AGREEMENTS_VIEW);
	}, []);

	return (
		<>
			<PolicyView onSubmit={handleAgreement} />
			<NoticeModalBottom
				visible={blockedVisible}
				onClose={() => setBlockedVisible(false)}
				onOk={handleMainlandBlockedConfirm}
				title={""}
				content={t(blockedMessageKey, { ns: ["survey", "common"] })}
				okText={t("cn-restriction-i-know", { ns: "common" })}
				align="center"
			/>
		</>
	);
}
